import _ from 'lodash';
import {
  ITask,
  TaskCustomer,
  TaskDirection,
  TaskOptions,
  TaskState,
} from './types';
import appManager from '@app/appManager';
import { authState } from '@modules/AuthModule/state';
import { resolveOrgNodeId } from '@modules/AuthModule/lib/resolveOrgNodeId';
import { v4 as uuidv4 } from 'uuid';
import {
  InteractionChannel,
  InteractionContactInput,
} from '@api/generated/graphql';

class Task implements ITask {
  private _id: string;
  // Stable across re-keys (updateTaskId) and reloads; views key off it.
  private readonly _viewKey: string;
  private _started = Date.now();
  private _state: TaskState;
  private _tenantId: string | null;
  private _type: string = 'voice';
  private _direction: TaskDirection = 'inbound';
  private _displayName: string | undefined;
  private _from: string = '';
  private _to: string | undefined;
  private _attributes: Record<string, any> = {};
  private _recoveryStartedAt: number | null;
  private _recoveryEndsAt: number | null;
  private _queueName: string | undefined;
  private _queueId: string | undefined;
  private _isInternal: boolean;
  private _isStateActive: boolean;
  private _loading: boolean;

  constructor(options: TaskOptions) {
    const {
      id,
      tenantId,
      from,
      to,
      startedAt = Date.now(),
      type = 'voice',
      direction = 'inbound',
      state = TaskState.Pending,
      displayName = '',
      attributes = {},
      recoveryStartedAt = null,
      recoveryEndsAt = null,
      queueName = undefined,
      queueId = undefined,
      isInternal = false,
      isStateActive = false,
      viewKey = uuidv4(),
    } = options;

    this._id = id;
    this._viewKey = viewKey;
    this._tenantId = tenantId ?? null;
    this._displayName = displayName;
    this._from = from;
    this._to = to;
    this._type = type;
    this._direction = direction;
    this._state = state;
    this._attributes = attributes;
    this._recoveryStartedAt = recoveryStartedAt;
    this._recoveryEndsAt = recoveryEndsAt;
    this._queueName = queueName;
    this._queueId = queueId;
    this._started = startedAt;
    this._isInternal = isInternal;
    this._isStateActive = isStateActive;
    this._loading = false;
  }

  public get id(): string {
    return this._id;
  }

  public get viewKey(): string {
    return this._viewKey;
  }

  public set id(id: string) {
    if (this._id === id) {
      return;
    }
    this._id = id;
    this.markAsUpdated('id');
  }

  public get type(): string {
    return this._type;
  }

  public set type(type: string) {
    if (this._type === type) {
      return;
    }
    this._type = type;
    this.markAsUpdated('type');
  }

  public get direction(): TaskDirection {
    return this._direction;
  }

  public get state(): TaskState {
    return this._state;
  }

  public set state(state: TaskState) {
    if (this._state === state) {
      return;
    }
    this._state = state;
    appManager.events.emit(`task.${state.toLowerCase()}`, this);
    this.markAsUpdated('state');
  }

  public get startedAt(): Date {
    return new Date(this._started);
  }

  public set startedAt(value: Date) {
    if (value.getTime() === this._started) {
      return;
    }
    this._started = value.getTime();
    this.markAsUpdated('startedAt');
  }

  public get tenantId(): string {
    return this._tenantId ?? '';
  }

  public set tenantId(tenantId: string) {
    if (this._tenantId === tenantId) {
      return;
    }
    this._tenantId = tenantId;
    this.markAsUpdated('tenantId');
  }

  public get displayName(): string {
    return this._displayName || this._from;
  }

  public set displayName(name: string | undefined) {
    if (this._displayName === name) {
      return;
    }
    this._displayName = name;
    this.markAsUpdated('displayName');
  }

  public set from(value: string) {
    if (this._from === value) {
      return;
    }
    this._from = value;
    this.markAsUpdated('from');
  }

  public get from(): string {
    return this._from;
  }

  public set to(value: string | undefined) {
    if (this._to === value) {
      return;
    }
    this._to = value;
    this.markAsUpdated('to');
  }

  public get to(): string | undefined {
    return this._to;
  }

  public get attributes(): Record<string, any> {
    return this._attributes;
  }

  public set attributes(value: Record<string, any>) {
    if (_.isEqual(this._attributes, value)) {
      return;
    }
    this._attributes = value;
    this.markAsUpdated('attributes');
  }

  public get recoveryStartedAt(): number | null {
    return this._recoveryStartedAt;
  }

  public set recoveryStartedAt(value: number | null) {
    if (this._recoveryStartedAt === value) {
      return;
    }
    this._recoveryStartedAt = value;
    this.markAsUpdated('recoveryStartedAt');
  }

  public get recoveryEndsAt(): number | null {
    return this._recoveryEndsAt;
  }

  public set recoveryEndsAt(value: number | null) {
    if (this._recoveryEndsAt === value) {
      return;
    }
    this._recoveryEndsAt = value;
    this.markAsUpdated('recoveryEndsAt');
  }

  public get queueName(): string | undefined {
    return this._queueName;
  }

  public set queueName(value: string | undefined) {
    if (this._queueName === value) {
      return;
    }
    this._queueName = value;
    this.markAsUpdated('queueName');
  }

  public get queueId(): string | undefined {
    return this._queueId;
  }

  public set queueId(value: string | undefined) {
    if (this._queueId === value) {
      return;
    }
    this._queueId = value;
    this.markAsUpdated('queueId');
  }

  public get loading(): boolean {
    return this._loading;
  }

  public set loading(value: boolean) {
    if (this._loading === value) {
      return;
    }
    this._loading = value;
    this.markAsUpdated('loading');
  }

  public get isInternal(): boolean {
    return this._isInternal;
  }

  public set isInternal(value: boolean) {
    if (this._isInternal === value) {
      return;
    }
    this._isInternal = value;
    this.markAsUpdated('isInternal');
  }

  public get isStateActive(): boolean {
    return this._isStateActive;
  }

  public set isStateActive(value: boolean) {
    if (this._isStateActive === value) {
      return;
    }
    this._isStateActive = value;
    this.markAsUpdated('isStateActive');
  }

  public get isOutbound(): boolean {
    return this._direction === 'outbound';
  }

  public get isTransfer(): boolean {
    return this._attributes?.conversationDetails?.isTransfer ?? false;
  }

  public get interactionChannel(): InteractionChannel {
    if (this._type === 'voice' && this._direction === 'inbound') {
      return InteractionChannel.InboundVoice;
    } else if (this._type === 'voice' && this._direction === 'outbound') {
      return InteractionChannel.OutboundVoice;
    } else if (this._type === 'text' && this._direction === 'inbound') {
      return InteractionChannel.InboundText;
    } else if (this._type === 'text' && this._direction === 'outbound') {
      return InteractionChannel.OutboundText;
    } else {
      return InteractionChannel.Unknown;
    }
  }

  public setCustomer(customer: TaskCustomer): void {
    const nextId = customer?.id;
    const nextName = customer?.name;
    // Written on every call so a stale org node or tenant never pairs with a
    // newer customer. The org node is the customer's own, else the task tenant's,
    // else cleared; the tenant id is set only by a pick that reports one.
    const nextTenantId = customer?.tenantId;
    const nextOrgNodeId = customer
      ? customer.orgNodeId ??
        resolveOrgNodeId(
          authState.user?.org.tenants ?? [],
          this._tenantId ?? '',
        )
      : undefined;

    // Normalize the empty-string default of `_displayName` to `undefined`
    // so a fresh task (`_displayName = ''`) compares equal to a clear-customer
    // call (`nextName = undefined`) and the guard short-circuits as intended.
    if (
      this._attributes.customerId === nextId &&
      (this._displayName === '' ? undefined : this._displayName) === nextName &&
      this._attributes.customerTenantId === nextTenantId &&
      this._attributes.customerOrgNodeId === nextOrgNodeId
    ) {
      return;
    }

    const {
      customerId: _prevId,
      customerTenantId: _prevTenantId,
      customerOrgNodeId: _prevOrgNodeId,
      ...rest
    } = this._attributes;
    this._attributes = {
      ...rest,
      ...(nextId !== undefined ? { customerId: nextId } : {}),
      ...(nextTenantId !== undefined ? { customerTenantId: nextTenantId } : {}),
      ...(nextOrgNodeId !== undefined
        ? { customerOrgNodeId: nextOrgNodeId }
        : {}),
    };
    this._displayName = nextName;
    this.markAsUpdated('customer');
  }

  /** Sets the interaction contacts attribute. Pass `undefined` to clear. */
  public setContacts(contacts: InteractionContactInput[] | undefined): void {
    if (_.isEqual(this._attributes.contacts, contacts)) {
      return;
    }
    if (contacts === undefined) {
      const { contacts: _prev, ...rest } = this._attributes;
      this._attributes = rest;
    } else {
      this._attributes = { ...this._attributes, contacts };
    }
    this.markAsUpdated('contacts');
  }

  private markAsUpdated(field: string) {
    appManager.events.emit(`task.updated`, { task: this, field });
  }
}

export default Task;
